import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, CheckCheck, AlertTriangle, Wrench, Camera, CreditCard, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import useBrowserNotifications from '../hooks/useBrowserNotifications';

// Notification sound - base64 encoded short beep
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkZuTgW9scHyGj5WQf21maHeChImIgXZwb3Z+hYmIhH95dXV4fIGEhYOAfXp5eXt9f4GCgYB+fHt7fH1+f4CAgH9+fXx8fH1+fn9/f39+fn19fX1+fn5/f39/fn5+fn5+fn5+f39/f39+fn5+fn5+fn9/f39/f35+fn5+fn5+f39/f39+fn5+fn5+fn9/f39/fn5+fn5+fn5/f39/f35+fn5+fn5+f39/f39+fn5+fn5+fn9/f39/fn5+fn5+fn5/f39/f35+fn5+fn5+f39/f39+fn5+';

export const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lastNotificationId, setLastNotificationId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('notificationSoundEnabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const dropdownRef = useRef(null);
    const bellRef = useRef(null);
    const audioRef = useRef(null);
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { isSupported, permission, requestPermission, showNotification } = useBrowserNotifications();

    // Initialize audio
    useEffect(() => {
        audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
        audioRef.current.volume = 0.5;
    }, []);

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Save sound preference
    useEffect(() => {
        localStorage.setItem('notificationSoundEnabled', JSON.stringify(soundEnabled));
    }, [soundEnabled]);

    // Play notification sound
    const playNotificationSound = useCallback(() => {
        if (soundEnabled && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
    }, [soundEnabled]);

    // Request notification permission on first render
    useEffect(() => {
        if (isSupported && permission === 'default') {
            // Delay asking for permission until user interacts
            const timeout = setTimeout(() => {
                requestPermission();
            }, 5000);
            return () => clearTimeout(timeout);
        }
    }, [isSupported, permission, requestPermission]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if clicking outside both the bell button and the dropdown
            const isClickInsideBell = bellRef.current && bellRef.current.contains(event.target);
            const isClickInsideDropdown = event.target.closest('[data-notification-dropdown]');
            
            if (!isClickInsideBell && !isClickInsideDropdown) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Fetch notifications on mount and periodically
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const response = await api.get('/notifications/unread-count');
            const newCount = response.data.count;
            
            // If count increased, there's a new notification - show browser notification and play sound
            if (newCount > unreadCount && unreadCount > 0) {
                // Play notification sound
                playNotificationSound();
                
                // Fetch the latest notification to show in browser
                const notifResponse = await api.get('/notifications?limit=1&unread_only=true');
                if (notifResponse.data.length > 0) {
                    const latestNotif = notifResponse.data[0];
                    if (latestNotif.id !== lastNotificationId) {
                        showNotification(latestNotif.title, {
                            body: latestNotif.message,
                            link: latestNotif.link,
                            tag: latestNotif.id
                        });
                        setLastNotificationId(latestNotif.id);
                    }
                }
            }
            
            setUnreadCount(newCount);
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await api.get('/notifications?limit=20');
            setNotifications(response.data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBellClick = () => {
        if (!isOpen) {
            fetchNotifications();
            // Calculate position for the dropdown
            if (bellRef.current && !isMobile) {
                const rect = bellRef.current.getBoundingClientRect();
                setDropdownPosition({
                    top: rect.bottom + 8,
                    left: rect.left
                });
            }
        }
        setIsOpen(!isOpen);
    };

    // Mark single notification as read (with stop propagation for button click)
    const handleMarkAsRead = async (e, notificationId) => {
        e.stopPropagation();
        await markAsRead(notificationId);
    };

    // Toggle sound
    const toggleSound = (e) => {
        e.stopPropagation();
        setSoundEnabled(prev => !prev);
    };

    const markAsRead = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/read`);
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.put('/notifications/mark-all-read');
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
            setIsOpen(false);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'damage_report':
                return <AlertTriangle className="w-4 h-4 text-amber-500" />;
            case 'maintenance_reminder':
                return <Wrench className="w-4 h-4 text-blue-500" />;
            case 'check_in':
                return <Camera className="w-4 h-4 text-green-500" />;
            case 'trial_expiration':
                return <CreditCard className="w-4 h-4 text-purple-500" />;
            default:
                return <Bell className="w-4 h-4 text-slate-500" />;
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                ref={bellRef}
                onClick={handleBellClick}
                className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="notification-bell"
            >
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-bounce">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown - rendered via portal to escape sidebar overflow */}
            {isOpen && createPortal(
                <>
                    {/* Mobile overlay */}
                    {isMobile && (
                        <div 
                            className="fixed inset-0 bg-black/50 z-[9998]"
                            onClick={() => setIsOpen(false)}
                        />
                    )}
                    <div 
                        data-notification-dropdown
                        className={`fixed bg-white shadow-xl border border-slate-200 overflow-hidden z-[9999] ${
                            isMobile 
                                ? 'inset-x-4 top-20 bottom-auto rounded-2xl max-h-[70vh]' 
                                : 'w-80 rounded-xl'
                        }`}
                        style={isMobile ? {} : { top: dropdownPosition.top, left: dropdownPosition.left }}
                    >
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                            <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-800">
                                    {language === 'el' ? 'Ειδοποιήσεις' : 'Notifications'}
                                </h3>
                                {/* Sound toggle */}
                                <button
                                    onClick={toggleSound}
                                    className={`p-1.5 rounded-full transition-colors ${
                                        soundEnabled 
                                            ? 'text-[#1B7EB5] hover:bg-[#1B7EB5]/10' 
                                            : 'text-slate-400 hover:bg-slate-100'
                                    }`}
                                    title={soundEnabled 
                                        ? (language === 'el' ? 'Απενεργοποίηση ήχου' : 'Mute sounds') 
                                        : (language === 'el' ? 'Ενεργοποίηση ήχου' : 'Enable sounds')
                                    }
                                >
                                    {soundEnabled ? (
                                        <Volume2 className="w-4 h-4" />
                                    ) : (
                                        <VolumeX className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="text-xs text-[#1B7EB5] hover:text-[#1B7EB5]/80 flex items-center gap-1"
                                    >
                                        <CheckCheck className="w-3.5 h-3.5" />
                                        {language === 'el' ? 'Όλα αναγνωσμένα' : 'Mark all read'}
                                    </button>
                                )}
                                {/* Close button for mobile */}
                                {isMobile && (
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="p-1 rounded-full hover:bg-slate-200 text-slate-500"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Notifications List */}
                        <div className={`overflow-y-auto ${isMobile ? 'max-h-[calc(70vh-120px)]' : 'max-h-96'}`}>
                            {loading ? (
                                <div className="p-4 text-center text-slate-500">
                                    <div className="animate-spin w-6 h-6 border-2 border-[#1B7EB5] border-t-transparent rounded-full mx-auto mb-2"></div>
                                    {language === 'el' ? 'Φόρτωση...' : 'Loading...'}
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="p-8 text-center">
                                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-500 text-sm">
                                        {language === 'el' ? 'Δεν υπάρχουν ειδοποιήσεις' : 'No notifications yet'}
                                    </p>
                                </div>
                            ) : (
                                notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                                            !notification.read ? 'bg-blue-50/50' : ''
                                        }`}
                                        data-testid={`notification-${notification.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5 flex-shrink-0">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>
                                                    {notification.title}
                                                </p>
                                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-slate-400 mt-1">
                                                    {formatTimeAgo(notification.created_at)}
                                                </p>
                                            </div>
                                            {/* Mark as read button */}
                                            {!notification.read && (
                                                <button
                                                    onClick={(e) => handleMarkAsRead(e, notification.id)}
                                                    className="flex-shrink-0 p-1.5 rounded-full hover:bg-[#1B7EB5]/10 text-[#1B7EB5] transition-colors"
                                                    title={language === 'el' ? 'Σήμανση ως αναγνωσμένο' : 'Mark as read'}
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
                                <button
                                    onClick={() => {
                                        navigate('/app/notifications');
                                        setIsOpen(false);
                                    }}
                                    className="text-sm text-[#1B7EB5] hover:text-[#1B7EB5]/80 w-full text-center font-medium"
                                >
                                    {language === 'el' ? 'Προβολή όλων' : 'View all notifications'}
                                </button>
                            </div>
                        )}
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default NotificationBell;
